'use client';
import * as React from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  InputAdornment,
  FormHelperText,
} from '@mui/material';

import { Visibility, VisibilityOff } from '@mui/icons-material';

import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormCard } from '../../ui/shared';
import { ChangePasswordFormInputs, changePasswordSchema } from '../types';
import { Changepassword } from '@/shared/i18n';
import { ChangePasswordEventKeys, ChangePasswordEvents } from '../events';
import { EventBus } from '@/shared/utils';
import { FormActionButton } from './elements';

interface ChangePasswordFormProps {
  eventBus: EventBus<ChangePasswordEvents>;
  dict: Changepassword;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ eventBus, dict }) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ChangePasswordFormInputs>({
    resolver: zodResolver(
      changePasswordSchema({
        minLengthMessage: dict.change_password.validation_password_to_short,
        numberRequiredMessage: dict.change_password.validation_number_required,
        passwordsMismatchMessage: dict.change_password.validation_password_mismatch,
      }),
    ),
    mode: 'onChange',
  });

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const onSubmit: SubmitHandler<ChangePasswordFormInputs> = React.useCallback(
    (data) =>
      eventBus.emit(ChangePasswordEventKeys.changePassword.update, { password: data.password }),
    [eventBus],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Box sx={{ mt: 2 }}>
        <FormCard title={dict.change_password.form_title}>
          <Grid container spacing={3}>
            {/* Password Field */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth error={!!errors.password}>
                <InputLabel htmlFor="outlined-adornment-password">
                  <Typography variant="s2">{dict.change_password.password_field_title}</Typography>
                </InputLabel>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <OutlinedInput
                      {...field}
                      id="outlined-adornment-password"
                      type={showPassword ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                      label={dict.change_password.password_field_title}
                    />
                  )}
                />
                <FormHelperText>
                  {errors.password?.message || dict.change_password.password_field_error_message}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Confirm Password Field */}
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth error={!!errors.confirmPassword}>
                <InputLabel htmlFor="outlined-adornment-confirm-password">
                  <Typography variant="s2">
                    {dict.change_password.password_confirm_field_title}
                  </Typography>
                </InputLabel>
                <Controller
                  name="confirmPassword"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <OutlinedInput
                      {...field}
                      id="outlined-adornment-confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                      label={dict.change_password.password_field_error_message}
                    />
                  )}
                />
                <FormHelperText>{errors.confirmPassword?.message}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </FormCard>
      </Box>

      {/* Submit Button */}
      <FormActionButton buttonText={dict.change_password.submit} isDisabled={!isValid} />
    </form>
  );
};

export { ChangePasswordForm };
